/**
 * The users list — `/countries/:cc/admin/users`. AUTHORED (no Figma exists for
 * the admin surface); the geometry follows the app's own table conventions
 * (`InquiryTable`: h-10 header band, py-2.5 rows, border-line separators).
 *
 * ===========================================================================
 * WHO SEES WHAT — THE LIST IS SERVER-NARROWED, AND THE PAGE SAYS SO
 * ===========================================================================
 * `GET /users` narrows per caller (`visibleUserIds`, users.service.ts:335): a
 * group admin sees everyone; a country-scoped `users.manage` holder sees only
 * users holding a role in a country they manage — and group admins (who hold
 * no role rows) are invisible to them. The client neither widens nor
 * re-narrows; it renders what it is given. The one client-side gate is the
 * honest-refusal screen for someone who reached the URL with no
 * `users.manage` anywhere — the `RequireCountry` doctrine one level down: the
 * API would 403 anyway; this buys a sentence instead of a screenful of errors.
 *
 * QUERY STATE LIVES IN THE URL (`?q=&status=&country=&page=`), the
 * inquiries-list precedent: a filtered view is a link a colleague can open,
 * and the Back button walks filter history rather than lying. `q` updates
 * with `replace` so six keystrokes are one history entry, and `page` resets
 * whenever a filter changes — page 3 of a different filter is nobody's intent.
 *
 * SEARCH IS THE SERVER'S (`?q=` — name/email LIKE, per ListUsersQueryDto), not
 * a client filter: the list is paginated, and filtering one page client-side
 * would "lose" a user who is on page 2, which on an admin screen reads as
 * "this account does not exist".
 *
 * THE TABLE HEADER diverges from `InquiryTable`'s in one deliberate way: its
 * labels are `text/secondary` on the plain surface, not `text/muted` on
 * `bg/subtle`. Muted (#9aa0aa) measures 2.63:1 on the light surface — an
 * inherited sub-AA pattern this NEW screen declines to replicate (2.5a
 * VISUAL-HONESTY precedent: fix locally, record globally). Measured pairs are
 * in `admin-users.contrast.test.ts`.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, Plus, Search, UserRound, X } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, EmptyState, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import { COUNTRY_ORDER, isCountryCode } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import { UserAvatar } from '../avatars/UserAvatar';
import { MenuItem } from './menu-item';
import { RoleChips, StatusChip } from './user-chips';
import { formatLastSignIn } from './user-format';
import { useAdminUsersList, useManageableCountries } from './use-admin-users';
import type { ListUsersQuery, UserStatus } from './users-api';

/** One page of the admin table. The server clamps anyway; 25 matches its default. */
const PER_PAGE = 25;

/* ───────────────────────────────────────────────── URL ↔ query-state mapping ── */

export interface UsersListState {
  q: string;
  status: UserStatus | null;
  country: CountryCode | null;
  page: number;
}

export function stateFromParams(params: URLSearchParams): UsersListState {
  const status = params.get('status');
  const country = params.get('country');
  const page = Number(params.get('page') ?? '1');
  return {
    q: params.get('q') ?? '',
    status: status === 'active' || status === 'disabled' ? status : null,
    country: isCountryCode(country) ? country : null,
    page: Number.isInteger(page) && page > 0 ? page : 1,
  };
}

export function paramsFromState(state: UsersListState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.q !== '') params.set('q', state.q);
  if (state.status !== null) params.set('status', state.status);
  if (state.country !== null) params.set('country', state.country);
  if (state.page > 1) params.set('page', String(state.page));
  return params;
}

/* ────────────────────────────────────────────────────────────── search field ── */

/**
 * The debounced `?q=` field — the `SearchField` pattern from the inquiries
 * toolbar (same 250ms, same "adjust state when the prop changes" mirror so the
 * Back button and Clear reset the box), re-authored here because features do
 * not import from each other's folders.
 */
function UserSearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (q: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  const [mirrored, setMirrored] = useState(value);

  if (value !== mirrored) {
    setMirrored(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onChange(draft), 250);
    return () => window.clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <div className="flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5">
      <Search aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Search name or email"
        aria-label="Search users by name or email"
        className="w-[220px] min-w-0 bg-transparent text-body13 text-fg outline-none placeholder:text-fg-muted"
      />
      {draft === '' ? null : (
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
          aria-label="Clear search"
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── filters ── */

const TAB_BASE =
  'flex h-[30px] shrink-0 items-center justify-center rounded-[6px] px-2.5 text-body13 transition-colors';
const TAB_ACTIVE = 'bg-surface text-fg shadow-tab-active';
const TAB_IDLE = 'text-fg-secondary hover:text-fg';

function StatusTabs({
  status,
  onSelect,
}: {
  status: UserStatus | null;
  onSelect: (status: UserStatus | null) => void;
}): React.JSX.Element {
  const tabs: Array<{ label: string; value: UserStatus | null }> = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Disabled', value: 'disabled' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter by status"
      className="flex items-center gap-0.5 rounded-sm bg-subtle p-[3px]"
    >
      {tabs.map((tab) => (
        <button
          key={tab.label}
          type="button"
          role="tab"
          aria-selected={status === tab.value}
          onClick={() => onSelect(tab.value)}
          className={cn(TAB_BASE, status === tab.value ? TAB_ACTIVE : TAB_IDLE)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function CountryFilter({
  country,
  onChange,
}: {
  country: CountryCode | null;
  onChange: (country: CountryCode | null) => void;
}): React.JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13',
          country === null ? 'text-fg-secondary hover:text-fg' : 'text-fg',
        )}
      >
        {country === null ? 'Country' : country}
        <ChevronDown aria-hidden="true" className="size-3.5" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 w-[200px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
        >
          <MenuItem onSelect={() => onChange(null)} selected={country === null}>
            All countries
          </MenuItem>
          {COUNTRY_ORDER.map((code) => (
            <MenuItem key={code} onSelect={() => onChange(code)} selected={country === code}>
              {code}
            </MenuItem>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/* ────────────────────────────────────────────────────────────────── the page ── */

export function UsersListPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { isGroupAdmin } = useSession();
  const manageable = useManageableCountries();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const state = stateFromParams(params);

  const query: ListUsersQuery = {
    page: state.page,
    perPage: PER_PAGE,
    q: state.q === '' ? undefined : state.q,
    status: state.status ?? undefined,
    country: state.country ?? undefined,
  };
  // The one client-side gate — see the header. `manageable` is empty exactly
  // when the caller holds users.manage nowhere and is not grp.
  const permitted = manageable.length > 0;
  const list = useAdminUsersList(query, permitted);

  const patch = (
    partial: Partial<UsersListState>,
    options: { replace?: boolean } = {},
  ): void => {
    // Every filter change resets the page — page 3 of a different filter is
    // nobody's intent, and a stale page silently shows "no results".
    const next = { ...state, page: 1, ...partial };
    setParams(paramsFromState(next), { replace: options.replace ?? false });
  };

  if (!permitted) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="User management is not available to this account">
          Managing users needs the users.manage permission in at least one country. Ask a
          group administrator if you believe you should have it.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const filtered = state.q !== '' || state.status !== null || state.country !== null;
  const from = (state.page - 1) * PER_PAGE + 1;
  const to = Math.min(state.page * PER_PAGE, total);
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  const detailHref = (id: number): string => `/countries/${countryCode}/admin/users/${String(id)}`;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Users"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(total)} ${total === 1 ? 'account' : 'accounts'}${
                isGroupAdmin ? '' : ' in the countries you manage'
              }`
        }
        actions={
          <Button onClick={() => navigate(`/countries/${countryCode}/admin/users/new`)}>
            <Plus aria-hidden="true" className="size-4" />
            New user
          </Button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusTabs status={state.status} onSelect={(status) => patch({ status })} />
        <div className="flex flex-wrap items-center gap-2">
          <UserSearchField value={state.q} onChange={(q) => patch({ q }, { replace: true })} />
          <CountryFilter country={state.country} onChange={(country) => patch({ country })} />
        </div>
      </div>

      {list.isPending ? (
        <SkeletonRows rows={8} label="Loading users…" />
      ) : list.isError ? (
        <Alert tone="danger" title="The user list could not be loaded">
          Nothing here was changed. Reload to try again.
        </Alert>
      ) : rows.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={<UserRound aria-hidden="true" className="size-5" />}
            title="No accounts match these filters"
            description="Search checks names and email addresses; the country filter checks role assignments."
            action={
              <Button
                variant="ghost"
                onClick={() => patch({ q: '', status: null, country: null })}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<UserRound aria-hidden="true" className="size-5" />}
            title="No user accounts yet"
            description="Create the first account and assign it a country role — it can sign in immediately."
            action={
              <Button onClick={() => navigate(`/countries/${countryCode}/admin/users/new`)}>
                <Plus aria-hidden="true" className="size-4" />
                New user
              </Button>
            }
          />
        )
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface">
          <table className="w-full min-w-[720px] border-collapse text-body13">
            <thead>
              <tr className="h-10 border-b border-line text-left">
                {/* text/secondary, not the muted TableHeader token — see header. */}
                <th scope="col" className="px-4 text-th uppercase text-fg-secondary">
                  User
                </th>
                <th scope="col" className="px-3 text-th uppercase text-fg-secondary">
                  Status
                </th>
                <th scope="col" className="px-3 text-th uppercase text-fg-secondary">
                  Roles
                </th>
                <th scope="col" className="px-3 text-th uppercase text-fg-secondary">
                  Last sign-in
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <tr
                  key={user.id}
                  className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle"
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={detailHref(user.id)}
                      className="flex min-w-0 items-center gap-2.5"
                    >
                      {/* decorative: the name is right beside it. */}
                      <UserAvatar
                        name={user.name}
                        userId={user.id}
                        size={32}
                        decorative
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-body font-medium text-fg">
                          {user.name}
                        </span>
                        <span className="truncate text-label text-fg-secondary">
                          {user.email}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusChip status={user.status} />
                  </td>
                  <td className="px-3 py-2.5">
                    <RoleChips roles={user.roles} isGroupAdmin={user.is_group_admin} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-fg-secondary">
                    <span className="tnum">{formatLastSignIn(user.last_login_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.data !== undefined && total > PER_PAGE ? (
        <div className="flex items-center justify-between">
          <p className="text-label text-fg-secondary">
            <span className="tnum">
              {String(from)}–{String(to)} of {String(total)}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              disabled={state.page <= 1}
              onClick={() => patch({ page: state.page - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              disabled={state.page >= lastPage}
              onClick={() => patch({ page: state.page + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
